import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, Save, Camera, Trash2, Info } from 'lucide-react';
import { LOJAS_AUDITAVEIS } from '../../../utils/empresas';

const API_BASE = '/api/auditoria/checklist';

// Replica das funções de score-utils.js (backend) para feedback em tempo real.
// O backend recalcula e é fonte da verdade — aqui é só UX.
// CONFORMIDADE: quanto MAIOR o %, melhor. ⚠️ Era RISCO até 2026-09-04 (o "Não"
// acumulava peso); virou para falar a mesma língua da Avaliação BSV e do ICP da
// Conservação — as faixas abaixo são as MESMAS do BSV, de propósito.
// Régua do método PESO (legado — versão.METODO === 'PESO').
const classificarConformidade = (p) => {
  if (p >= 0.90) return { nome: 'Excelente', cor: '#059669', bg: '#d1fae5', icone: '🟢' };
  if (p >= 0.80) return { nome: 'Satisfatório', cor: '#65a30d', bg: '#ecfccb', icone: '🟢' };
  if (p >= 0.60) return { nome: 'Atenção', cor: '#d97706', bg: '#fef3c7', icone: '🟡' };
  return { nome: 'Crítico', cor: '#dc2626', bg: '#fee2e2', icone: '🔴' };
};

// DEM-00011 (T15): régua do método CONTAGEM (versão.METODO === 'CONTAGEM') —
// a MESMA da Avaliação 360/SA/TSV (server/operacional/aval360/aval360-score.js
// › classificar360). Cortes diferentes do PESO (70 no lugar de 60), de propósito.
const classificarContagem = (p) => {
  if (p >= 0.90) return { nome: 'Verde', cor: '#059669', bg: '#d1fae5', icone: '🟢' };
  if (p >= 0.80) return { nome: 'Amarelo', cor: '#ca8a04', bg: '#fef9c3', icone: '🟡' };
  if (p >= 0.70) return { nome: 'Laranja', cor: '#c2410c', bg: '#ffedd5', icone: '🟠' };
  return { nome: 'Vermelho', cor: '#dc2626', bg: '#fee2e2', icone: '🔴' };
};

// Estilo da área marcada como Não Aplicável (fora do cálculo).
const CONF_NA = { nome: 'N/A', cor: '#475569', bg: '#f1f5f9', icone: '⚪' };

// Redimensiona a foto NO NAVEGADOR antes de subir: lado maior ≤ 1280px, JPEG
// qualidade 0.7. Devolve um data URI base64 (string). Sem biblioteca — evita
// foto de celular de 3-5 MB inflar o PDF e o SharePoint; o backend só decodifica.
const MAX_LADO = 1280;
const redimensionarImagem = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > MAX_LADO) {
          height = Math.round((height * MAX_LADO) / width);
          width = MAX_LADO;
        } else if (height > MAX_LADO) {
          width = Math.round((width * MAX_LADO) / height);
          height = MAX_LADO;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const ChecklistQuestionario = ({ idVersao, onVoltar }) => {
  const [versao, setVersao] = useState(null);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  // Cabeçalho da auditoria
  const cabecalhoVazio = () => ({
    loja: '', data_auditoria: new Date().toISOString().substring(0, 10),
    auditor_responsavel: '', gerente_plantao: '', gerente_negocios: '',
    recomendacoes: '',
  });
  const [cabecalho, setCabecalho] = useState(cabecalhoVazio());
  const [gerentesPorLoja, setGerentesPorLoja] = useState({});

  // Sem recorte por mercado: a auditoria financeira/estoques cobre todas as lojas
  // auditáveis. A divisão Norte/Sul (cross-avaliação) ficou só na 360 (DEM-00034).

  // respostas[idPergunta] = { atende: 1|0, observacao: '', imagem, naoAplicavel }
  // naoAplicavel só é possível em pergunta com PERMITE_NA: ela sai do cálculo da
  // conformidade (não pontua e seu peso fica fora do denominador) e não exige Sim/Não.
  const [respostas, setRespostas] = useState({});

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const token = localStorage.getItem('authToken');
      const [rVersao, rGerentes] = await Promise.all([
        fetch(`${API_BASE}/versao/${idVersao}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/gerentes`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (!rVersao.ok) throw new Error('Falha ao carregar versão.');
      const data = await rVersao.json();
      setVersao(data.versao);
      setAreas(data.areas || []);
      if (rGerentes.ok) setGerentesPorLoja(await rGerentes.json());
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }, [idVersao]);

  useEffect(() => { carregar(); }, [carregar]);

  // Pré-popula cabeçalho com auditor logado
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setCabecalho((c) => ({ ...c, auditor_responsavel: c.auditor_responsavel || user.nome || '' }));
  }, []);

  // DEM-00011 (T15): no método CONTAGEM cada pergunta vale 1 (o peso da
  // pergunta é ignorado); no PESO (legado) vale o peso cadastrado. É só isto
  // que muda entre os dois métodos — o resto da conta (N/A fora do
  // denominador, área 100% N/A vira "N/A") é igual.
  const metodoContagem = versao?.METODO === 'CONTAGEM';
  const classificar = metodoContagem ? classificarContagem : classificarConformidade;

  // Conformidade em tempo real, por área e total — calculada POR PERGUNTA.
  // Pergunta marcada N/A: não pontua e seu peso/contagem SAI do denominador.
  // Área sem nenhuma pergunta aplicável (todas N/A) → exibida como "N/A".
  const resumo = useMemo(() => {
    let scoreTotal = 0;
    let pesoAplicavel = 0;
    const porArea = areas.map((a) => {
      let scoreArea = 0;
      let pesoArea = 0;
      let temAplicavel = false;
      for (const p of a.perguntas) {
        const r = respostas[p.ID];
        if (r?.naoAplicavel) continue; // N/A: fora do cálculo
        temAplicavel = true;
        const valor = metodoContagem ? 1 : p.PESO;
        pesoArea += valor;
        if (r && r.atende === 1) scoreArea += valor; // conformidade: o "Sim" pontua
      }
      scoreTotal += scoreArea;
      pesoAplicavel += pesoArea;
      if (!temAplicavel) {
        return { idArea: a.ID, scoreArea: 0, pesoArea: 0, pctArea: 0, na: true, classArea: CONF_NA };
      }
      const pctArea = pesoArea > 0 ? scoreArea / pesoArea : 0;
      return { idArea: a.ID, scoreArea, pesoArea, pctArea, na: false, classArea: classificar(pctArea) };
    });
    const pctTotal = pesoAplicavel > 0 ? scoreTotal / pesoAplicavel : 0;
    return {
      scoreTotal, porArea, pctTotal,
      classTotal: pesoAplicavel > 0 ? classificar(pctTotal) : CONF_NA,
      pesoMax: pesoAplicavel, // peso (ou contagem) aplicável — já desconta N/A
    };
  }, [areas, respostas, metodoContagem, classificar]);

  const totalPerguntas = useMemo(
    () => areas.reduce((acc, a) => acc + a.perguntas.length, 0),
    [areas]
  );
  // Pergunta conta como respondida se foi marcada N/A OU tem Sim/Não.
  const totalRespondidas = useMemo(() => {
    let n = 0;
    for (const a of areas) {
      for (const p of a.perguntas) {
        const r = respostas[p.ID];
        if (r && (r.naoAplicavel || r.atende === 0 || r.atende === 1)) n++;
      }
    }
    return n;
  }, [areas, respostas]);

  const responder = (idPergunta, atende) => {
    // Marcar Sim/Não desfaz o N/A. Ao marcar "Sim" (atende=1), descarta a foto
    // (evidência só faz sentido em negativa).
    setRespostas((r) => ({
      ...r,
      [idPergunta]: {
        ...r[idPergunta],
        atende,
        naoAplicavel: false,
        imagem: atende === 1 ? null : r[idPergunta]?.imagem,
      },
    }));
  };
  const observar = (idPergunta, observacao) => {
    setRespostas((r) => ({ ...r, [idPergunta]: { ...r[idPergunta], observacao } }));
  };

  // Anexa a foto de evidência (redimensionada) a uma pergunta negativa.
  const anexarImagem = async (idPergunta, file) => {
    if (!file) return;
    try {
      const dataUrl = await redimensionarImagem(file);
      setRespostas((r) => ({ ...r, [idPergunta]: { ...r[idPergunta], imagem: dataUrl } }));
    } catch {
      setErro('Não foi possível processar a imagem. Tente outra foto.');
    }
  };
  const removerImagem = (idPergunta) => {
    setRespostas((r) => ({ ...r, [idPergunta]: { ...r[idPergunta], imagem: null } }));
  };

  // Liga/desliga o "Não Aplicável" de uma pergunta (só perguntas com PERMITE_NA).
  // Ao ligar, limpa Sim/Não e a foto (a pergunta sai do cálculo).
  const alternarNa = (idPergunta) => {
    setRespostas((r) => {
      const atual = r[idPergunta] || {};
      const liga = !atual.naoAplicavel;
      return {
        ...r,
        [idPergunta]: {
          ...atual,
          naoAplicavel: liga,
          atende: liga ? undefined : atual.atende,
          imagem: liga ? null : atual.imagem,
        },
      };
    });
  };

  const submeter = async () => {
    if (!cabecalho.loja || !cabecalho.data_auditoria) {
      setErro('Loja e data são obrigatórios.');
      return;
    }
    if (totalRespondidas < totalPerguntas) {
      setErro(`Responda todas as ${totalPerguntas} perguntas antes de salvar. (${totalRespondidas}/${totalPerguntas})`);
      return;
    }
    setSalvando(true);
    setErro('');
    setSucesso('');
    try {
      const token = localStorage.getItem('authToken');
      // Monta a partir de TODAS as perguntas (o backend exige todas respondidas).
      // Pergunta N/A entra com nao_aplicavel=1 e atende=1 (placeholder).
      const respostasPayload = [];
      for (const a of areas) {
        for (const p of a.perguntas) {
          const r = respostas[p.ID] || {};
          const na = !!r.naoAplicavel;
          respostasPayload.push({
            id_pergunta: p.ID,
            atende: na ? 1 : r.atende,
            observacao: na ? null : (r.observacao || null),
            imagem: na ? null : (r.imagem || null),
            nao_aplicavel: na ? 1 : 0,
          });
        }
      }
      const payload = { id_versao: idVersao, ...cabecalho, respostas: respostasPayload };
      const r = await fetch(`${API_BASE}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || 'Falha ao salvar.');
      setSucesso(`Auditoria #${data.idAuditoria} salva — Score ${data.scoreTotal} (${data.percentualConformidade}%) · ${data.classificacao}`);
      // Limpa o formulário para iniciar uma nova auditoria sem recarregar a tela
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setCabecalho({ ...cabecalhoVazio(), auditor_responsavel: user.nome || '' });
      setRespostas({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  if (loading) return <p style={{ color: '#64748b' }}>Carregando checklist…</p>;
  if (erro && !versao) return <p style={{ color: '#dc2626' }}>{erro}</p>;

  // Filtro de loja restrito às empresas auditáveis (HUNGER, P&G, P&M, GC FOOD).
  const gerentesDaLoja = cabecalho.loja ? gerentesPorLoja[cabecalho.loja] : null;

  const botaoSimNao = (selecionado, cor) => ({
    flex: 1, padding: '8px 12px', borderRadius: 8,
    border: `2px solid ${selecionado ? cor : '#e2e8f0'}`,
    background: selecionado ? cor : '#fff',
    color: selecionado ? '#fff' : '#475569',
    fontWeight: 700, cursor: 'pointer', fontSize: 13, transition: 'all .12s',
  });

  return (
    <div>
      {/* Voltar + título */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button
          onClick={onVoltar}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
            borderRadius: 8, fontWeight: 700, cursor: 'pointer',
          }}
        >
          <ArrowLeft size={16} /> Voltar
        </button>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          {versao?.NOME_VERSAO}
        </h3>
        {/* Diz qual método/régua vale nesta versão (T19) — sem isto, um "80%"
            de contagem pareceria comparável a um "80%" de peso. */}
        {versao && (
          <span style={{
            padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 800,
            background: metodoContagem ? '#d1fae5' : '#f1f5f9',
            color: metodoContagem ? '#065f46' : '#475569',
          }}>
            {metodoContagem ? 'Contagem pura (sem peso)' : 'Peso (legado)'}
          </span>
        )}
      </div>

      {erro && (
        <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>
          {erro}
        </div>
      )}
      {sucesso && (
        <div style={{ padding: 12, background: '#d1fae5', color: '#065f46', borderRadius: 8, marginBottom: 12, fontWeight: 700 }}>
          ✅ {sucesso}
        </div>
      )}

      {/* Cabeçalho */}
      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
        padding: 20, marginBottom: 20,
      }}>
        <h4 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 800, color: '#475569' }}>Dados da Auditoria</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Loja *</label>
            <select
              value={cabecalho.loja}
              onChange={(e) => setCabecalho({ ...cabecalho, loja: e.target.value, gerente_unidade: '', gerente_plantao: '' })}
              style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }}
            >
              <option value="">— escolher —</option>
              {LOJAS_AUDITAVEIS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Data *</label>
            <input
              type="date" value={cabecalho.data_auditoria}
              onChange={(e) => setCabecalho({ ...cabecalho, data_auditoria: e.target.value })}
              style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Responsável (Auditor)</label>
            <input
              type="text" value={cabecalho.auditor_responsavel}
              onChange={(e) => setCabecalho({ ...cabecalho, auditor_responsavel: e.target.value })}
              style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Gerente de Negócios</label>
            <select
              value={cabecalho.gerente_negocios}
              onChange={(e) => setCabecalho({ ...cabecalho, gerente_negocios: e.target.value })}
              disabled={!gerentesDaLoja}
              style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }}
            >
              <option value="">{gerentesDaLoja ? '— escolher —' : 'Escolha a loja primeiro'}</option>
              {(gerentesDaLoja?.negocios || []).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Gerente de Plantão</label>
            <select
              value={cabecalho.gerente_plantao}
              onChange={(e) => setCabecalho({ ...cabecalho, gerente_plantao: e.target.value })}
              disabled={!gerentesDaLoja}
              style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }}
            >
              <option value="">{gerentesDaLoja ? '— escolher —' : 'Escolha a loja primeiro'}</option>
              {(gerentesDaLoja?.plantao || []).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Score em tempo real (sticky) */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 5,
        background: resumo.classTotal.bg,
        border: `2px solid ${resumo.classTotal.cor}`,
        borderRadius: 12, padding: 16, marginBottom: 20,
        display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: 28 }}>{resumo.classTotal.icone}</div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', letterSpacing: 0.5 }}>SCORE GERAL</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: resumo.classTotal.cor }}>
            {resumo.scoreTotal} / {resumo.pesoMax} ·  {(resumo.pctTotal * 100).toFixed(2)}% · {resumo.classTotal.nome}
          </div>
        </div>
        <div style={{ fontSize: 13, color: '#475569', fontWeight: 700 }}>
          Respondidas: {totalRespondidas} / {totalPerguntas}
        </div>
      </div>

      {/* Aviso sobre as fotos de evidência: elas vão SÓ para o PDF da auditoria,
          não há galeria/visualização das imagens aqui no site. */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
        padding: '12px 14px', marginBottom: 20, color: '#1e40af', fontSize: 13,
      }}>
        <Info size={18} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          As fotos anexadas às perguntas marcadas como <strong>“Não”</strong> aparecem
          <strong> somente no PDF</strong> da auditoria. <strong>Não há visualização das imagens aqui no site</strong> —
          para vê-las, baixe o PDF em “Auditorias gravadas”.
        </span>
      </div>

      {/* Áreas */}
      {areas.map((a, idx) => {
        const r = resumo.porArea[idx];
        return (
          <div key={a.ID} style={{
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
            padding: 20, marginBottom: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
              <h4 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
                {idx + 1}. {a.NOME}
              </h4>
              <div style={{
                padding: '4px 10px', borderRadius: 8, fontSize: 13, fontWeight: 800,
                background: r.classArea.bg, color: r.classArea.cor,
              }}>
                {r.na
                  ? `${r.classArea.icone} N/A`
                  : `${r.classArea.icone} ${r.scoreArea}/${r.pesoArea} · ${(r.pctArea * 100).toFixed(0)}%`}
              </div>
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              {a.perguntas.map((p) => {
                const resp = respostas[p.ID] || {};
                return (
                  <div key={p.ID} style={{
                    padding: 12, background: '#f8fafc', borderRadius: 8,
                    display: 'grid', gridTemplateColumns: '1fr 200px', gap: 12, alignItems: 'start',
                  }}>
                    <div>
                      <div style={{ color: '#0f172a', fontSize: 14 }}>{p.TEXTO}</div>
                      {/* No método CONTAGEM o peso da pergunta é ignorado no cálculo
                          (cada uma vale 1) — mostrá-lo sugeriria que ele ainda pesa. */}
                      {!metodoContagem && (
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Peso: {p.PESO}</div>
                      )}
                      {resp.naoAplicavel && (
                        <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: '#64748b' }}>
                          ⚪ Não aplicável — fora do cálculo da conformidade.
                        </div>
                      )}
                      {!resp.naoAplicavel && (
                        <input
                          type="text" placeholder="Observação (opcional)"
                          value={resp.observacao || ''}
                          onChange={(e) => observar(p.ID, e.target.value)}
                          style={{ width: '100%', marginTop: 6, padding: 6, fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 6 }}
                        />
                      )}
                      {/* Evidência fotográfica — só na pergunta marcada como "Não" (opcional) */}
                      {resp.atende === 0 && !resp.naoAplicavel && (
                        <div style={{ marginTop: 8 }}>
                          {resp.imagem ? (
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                              <img
                                src={resp.imagem} alt="Evidência"
                                style={{ maxWidth: 120, maxHeight: 90, borderRadius: 6, border: '1px solid #e2e8f0' }}
                              />
                              <button
                                type="button" onClick={() => removerImagem(p.ID)}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 4,
                                  padding: '6px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                  background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 6,
                                }}
                              >
                                <Trash2 size={13} /> Remover
                              </button>
                            </div>
                          ) : (
                            <label
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                padding: '6px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 6,
                              }}
                            >
                              <Camera size={14} /> Anexar foto (opcional)
                              <input
                                type="file" accept="image/*" capture="environment"
                                onChange={(e) => anexarImagem(p.ID, e.target.files?.[0])}
                                style={{ display: 'none' }}
                              />
                            </label>
                          )}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button onClick={() => responder(p.ID, 1)} style={botaoSimNao(resp.atende === 1, '#059669')}>
                        Sim
                      </button>
                      <button onClick={() => responder(p.ID, 0)} style={botaoSimNao(resp.atende === 0, '#dc2626')}>
                        Não
                      </button>
                      {/* 3º botão só nas perguntas marcadas como podáveis N/A na versão */}
                      {p.PERMITE_NA === 1 && (
                        <button onClick={() => alternarNa(p.ID)} style={botaoSimNao(resp.naoAplicavel === true, '#64748b')}>
                          N/A
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Recomendações + Salvar */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <label style={{ fontSize: 14, fontWeight: 700, color: '#475569' }}>Recomendações e Plano de Ação</label>
        <textarea
          rows={4} value={cabecalho.recomendacoes}
          onChange={(e) => setCabecalho({ ...cabecalho, recomendacoes: e.target.value })}
          style={{ width: '100%', marginTop: 8, padding: 10, border: '1px solid #cbd5e1', borderRadius: 6, fontFamily: 'inherit' }}
        />
      </div>

      <button
        onClick={submeter} disabled={salvando}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '12px 24px', background: salvando ? '#94a3b8' : '#059669',
          color: '#fff', border: 'none', borderRadius: 8,
          fontWeight: 800, fontSize: 15, cursor: salvando ? 'wait' : 'pointer',
        }}
      >
        <Save size={18} /> {salvando ? 'Salvando…' : 'Salvar Auditoria'}
      </button>
    </div>
  );
};

export default ChecklistQuestionario;
